/**
 * Roulette game-domain types.
 * Ported from packages/amgroulette_v2/lib/core/constants/ and
 * packages/amgroulette_v2/lib/domain/.
 */

export type GamePhase =
  | 'idle'
  | 'initializing'
  | 'modeSelection'
  | 'betting'
  | 'bettingClosed'
  | 'spinning'
  | 'showResult'
  | 'result'
  | 'newRound'
  | 'error';

/**
 * BetType is the numeric `betID` emitted by the betting Rive (55..212).
 * See `chipsPosition` in
 *   packages/amgroulette_v2/lib/presentation/flame/roulette_flame_game.dart
 * for the full list of 162 areas.
 */
export type BetId = number;

/** Bet-type categories — used for payout calculation when settling locally. */
export type BetCategory =
  | 'straight' // single number (incl. 0)
  | 'split'    // 2 numbers
  | 'street'   // 3 numbers
  | 'corner'   // 4 numbers
  | 'sixline'  // 6 numbers
  | 'column'   // 12 numbers (column / dozen)
  | 'evenmoney'; // red/black/odd/even/1-18/19-36

/**
 * Roulette payouts (european single-zero, "to one" style).
 * Multiplier returned to player on win = `payout + 1` (their stake back + winnings).
 */
export const PAYOUT_MULTIPLIER: Record<BetCategory, number> = {
  straight: 35,
  split: 17,
  street: 11,
  corner: 8,
  sixline: 5,
  column: 2,
  evenmoney: 1,
};

/** Red numbers on a European wheel. */
export const RED_NUMBERS = new Set<number>([
  1, 3, 5, 7, 9, 12, 14, 16, 18, 19, 21, 23, 25, 27, 30, 32, 34, 36,
]);
export function isRed(n: number): boolean { return RED_NUMBERS.has(n); }
export function isBlack(n: number): boolean { return n !== 0 && !RED_NUMBERS.has(n); }
export function isEven(n: number): boolean { return n !== 0 && n % 2 === 0; }
export function isOdd(n: number): boolean { return n !== 0 && n % 2 === 1; }
export function isLow(n: number): boolean { return n >= 1 && n <= 18; }
export function isHigh(n: number): boolean { return n >= 19 && n <= 36; }
export function dozenOf(n: number): 1 | 2 | 3 | null {
  if (n >= 1 && n <= 12) return 1;
  if (n >= 13 && n <= 24) return 2;
  if (n >= 25 && n <= 36) return 3;
  return null;
}
export function columnOf(n: number): 1 | 2 | 3 | null {
  if (n === 0) return null;
  const m = n % 3;
  if (m === 1) return 1;
  if (m === 2) return 2;
  return 3;
}

export interface GameConfig {
  minBet: number;
  maxBet: number;
  chipValues: readonly number[];
}

export interface RouletteResult {
  /** Winning number 0..36. */
  winningNumber: number;
}

export interface GameRound {
  roundId: string;
  nextRoundId: string;
  winningNumber: number;
  totalWinLose: number;
  totalBet: number;
  positiveWinAmount: number;
  hasPositiveWin: boolean;
}

/**
 * Mapping from a bet ID (55..212) to the list of roulette numbers it covers
 * + the category (used for payout). Mirrors the comments in
 * roulette_flame_game.dart chipsPosition — each entry's comment lists the
 * numbers (e.g. `// 4, 5` for bet97).
 */
export interface BetIdInfo {
  category: BetCategory;
  numbers: readonly number[];
}

export const BET_ID_INFO: ReadonlyMap<number, BetIdInfo> = (() => {
  const m = new Map<number, BetIdInfo>();
  // Straight up — 0 then 1..36, in the order they appear in chipsPosition.
  // bet55 = 0; bet56..bet91 = numbers 1..36 in the table layout.
  const straightOrder = [
    0, 3, 2, 1,
    6, 5, 4,
    9, 8, 7,
    12, 11, 10,
    15, 14, 13,
    18, 17, 16,
    21, 20, 19,
    24, 23, 22,
    27, 26, 25,
    30, 29, 28,
    33, 32, 31,
    36, 35, 34,
  ];
  straightOrder.forEach((n, i) => {
    m.set(55 + i, { category: 'straight', numbers: [n] });
  });

  // Splits (92..148) — per the comments in roulette_flame_game.dart.
  const splits: ReadonlyArray<[number, readonly number[]]> = [
    [92, [1, 2]], [93, [1, 4]], [94, [2, 3]], [95, [2, 5]], [96, [3, 6]],
    [97, [4, 5]], [98, [4, 7]], [99, [5, 6]], [100, [5, 8]], [101, [6, 9]],
    [102, [7, 8]], [103, [7, 10]], [104, [8, 9]], [105, [8, 11]], [106, [9, 12]],
    [107, [10, 11]], [108, [10, 13]], [109, [11, 12]], [110, [11, 14]], [111, [12, 15]],
    [112, [13, 14]], [113, [13, 16]], [114, [14, 15]], [115, [14, 17]], [116, [15, 18]],
    [117, [16, 17]], [118, [16, 19]], [119, [17, 18]], [120, [17, 20]], [121, [18, 21]],
    [122, [19, 20]], [123, [19, 22]], [124, [20, 21]], [125, [20, 23]], [126, [21, 24]],
    [127, [22, 23]], [128, [22, 25]], [129, [23, 24]], [130, [23, 26]], [131, [24, 27]],
    [132, [25, 26]], [133, [25, 28]], [134, [26, 27]], [135, [26, 29]], [136, [27, 30]],
    [137, [28, 29]], [138, [28, 31]], [139, [29, 30]], [140, [29, 32]], [141, [30, 33]],
    [142, [31, 32]], [143, [31, 34]], [144, [32, 33]], [145, [32, 35]], [146, [33, 36]],
    [147, [34, 35]], [148, [35, 36]],
  ];
  for (const [id, ns] of splits) m.set(id, { category: 'split', numbers: ns });

  // Streets (149..162). Note 149 is "0,1,2" and 150 is "0,2,3" — both are
  // street-style 3-number bets that include the zero.
  const streets: ReadonlyArray<[number, readonly number[]]> = [
    [149, [0, 1, 2]], [150, [0, 2, 3]],
    [151, [1, 2, 3]], [152, [4, 5, 6]], [153, [7, 8, 9]], [154, [10, 11, 12]],
    [155, [13, 14, 15]], [156, [16, 17, 18]], [157, [19, 20, 21]], [158, [22, 23, 24]],
    [159, [25, 26, 27]], [160, [28, 29, 30]], [161, [31, 32, 33]], [162, [34, 35, 36]],
  ];
  for (const [id, ns] of streets) m.set(id, { category: 'street', numbers: ns });

  // Corners (163..185). 163 is the basket "0,1,2,3" (paid as corner here).
  const corners: ReadonlyArray<[number, readonly number[]]> = [
    [163, [0, 1, 2, 3]],
    [164, [1, 2, 4, 5]], [165, [2, 3, 5, 6]],
    [166, [4, 5, 7, 8]], [167, [5, 6, 8, 9]],
    [168, [7, 8, 10, 11]], [169, [8, 9, 11, 12]],
    [170, [10, 11, 13, 14]], [171, [11, 12, 14, 15]],
    [172, [13, 14, 16, 17]], [173, [14, 15, 17, 18]],
    [174, [16, 17, 19, 20]], [175, [17, 18, 20, 21]],
    [176, [19, 20, 22, 23]], [177, [20, 21, 23, 24]],
    [178, [22, 23, 25, 26]], [179, [23, 24, 26, 27]],
    [180, [25, 26, 28, 29]], [181, [26, 27, 29, 30]],
    [182, [28, 29, 31, 32]], [183, [29, 30, 32, 33]],
    [184, [31, 32, 34, 35]], [185, [32, 33, 35, 36]],
  ];
  for (const [id, ns] of corners) m.set(id, { category: 'corner', numbers: ns });

  // Six-lines (186..196) — two adjacent streets.
  for (let i = 0; i <= 10; i++) {
    const start = 1 + i * 3;
    const nums: number[] = [];
    for (let k = 0; k < 6; k++) nums.push(start + k);
    m.set(186 + i, { category: 'sixline', numbers: nums });
  }

  // 2-to-1 columns (198..200): col1 = {1,4,7,...,34}; col2 = ...; col3 = ...
  // Per chipsPosition comments (bet198=1_2to1, bet199=2_2to1, bet200=3_2to1).
  const col1: number[] = [];
  const col2: number[] = [];
  const col3: number[] = [];
  for (let n = 1; n <= 36; n++) {
    const c = columnOf(n);
    if (c === 1) col1.push(n);
    else if (c === 2) col2.push(n);
    else col3.push(n);
  }
  // Note: in chipsPosition, bet198 is the BOTTOM row, which is column 3
  // (numbers 3,6,9...,36). The labels are confusing — Flutter's are by
  // visual position. We map by visual row order.
  m.set(198, { category: 'column', numbers: col3 }); // bottom row
  m.set(199, { category: 'column', numbers: col2 }); // middle row
  m.set(200, { category: 'column', numbers: col1 }); // top row

  // Dozens (201..203).
  const d1: number[] = []; const d2: number[] = []; const d3: number[] = [];
  for (let n = 1; n <= 12; n++) d1.push(n);
  for (let n = 13; n <= 24; n++) d2.push(n);
  for (let n = 25; n <= 36; n++) d3.push(n);
  m.set(201, { category: 'column', numbers: d1 });
  m.set(202, { category: 'column', numbers: d2 });
  m.set(203, { category: 'column', numbers: d3 });

  // Even-money bets (204..209) per chipsPosition comments.
  const low: number[] = []; for (let n = 1; n <= 18; n++) low.push(n);
  const high: number[] = []; for (let n = 19; n <= 36; n++) high.push(n);
  const reds: number[] = Array.from(RED_NUMBERS).sort((a, b) => a - b);
  const blacks: number[] = [];
  for (let n = 1; n <= 36; n++) if (!RED_NUMBERS.has(n)) blacks.push(n);
  const odds: number[] = []; for (let n = 1; n <= 36; n++) if (n % 2 === 1) odds.push(n);
  const evens: number[] = []; for (let n = 1; n <= 36; n++) if (n % 2 === 0) evens.push(n);
  m.set(204, { category: 'evenmoney', numbers: low });
  m.set(205, { category: 'evenmoney', numbers: high });
  m.set(206, { category: 'evenmoney', numbers: reds });
  m.set(207, { category: 'evenmoney', numbers: blacks });
  m.set(208, { category: 'evenmoney', numbers: odds });
  m.set(209, { category: 'evenmoney', numbers: evens });

  // Splits with zero (210..212).
  m.set(210, { category: 'split', numbers: [0, 1] });
  m.set(211, { category: 'split', numbers: [0, 2] });
  m.set(212, { category: 'split', numbers: [0, 3] });

  return m;
})();

export function betCovers(betId: number, winningNumber: number): boolean {
  const info = BET_ID_INFO.get(betId);
  return info != null && info.numbers.includes(winningNumber);
}

export function payoutMultiplierFor(betId: number): number {
  const info = BET_ID_INFO.get(betId);
  if (!info) return 0;
  return PAYOUT_MULTIPLIER[info.category];
}
